import React, { useRef, useState, useEffect, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sankey, sankeyLinkHorizontal } from "d3-sankey";
import * as d3 from "d3";
import { SizeMe } from "react-sizeme";

interface SankeyNode extends d3.SimulationNodeDatum {
  name: string;
  category: string;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface SankeyLink {
  source: any;
  target: any;
  value: number;
}

export interface SankeyData {
  nodes: Array<{ name: string; category: string; type: any }>;
  links: Array<{ source: number; target: number; value: number }>;
}

export interface ChartFilterChange {
  selectedCategory?: string;
  selectedAccount?: string;
  tx1Account?: string;
  tx2Account?: string;
  linkFilterSource?: string;
  linkFilterDest?: string;
}

function reorganizeNodes(data: any): SankeyData {
  if (!data?.nodes?.length || !Array.isArray(data.links)) {
    return { nodes: [], links: [] };
  }

  const valueByIndex: Record<number, number> = {};
  data.nodes.forEach((_, i) => (valueByIndex[i] = 0));
  data.links.forEach((link) => {
    if (
      link.source != null &&
      link.target != null &&
      data.nodes[link.source] &&
      data.nodes[link.target]
    ) {
      const val = Math.abs(Number(link.value) || 0);
      valueByIndex[link.source] = (valueByIndex[link.source] || 0) + val;
      valueByIndex[link.target] = (valueByIndex[link.target] || 0) + val;
    }
  });

  const incomeNodes = data.nodes
    .map((node, index) => ({ node, index, value: valueByIndex[index] || 0 }))
    .filter(({ node }) => node.category === "income")
    .sort((a, b) => b.value - a.value)
    .map(({ node }) => node);
  const accountNodes = data.nodes.filter((n) => n.category === "account");
  const expenseNodes = data.nodes
    .map((node, index) => ({ node, index, value: valueByIndex[index] || 0 }))
    .filter(({ node }) => node.category === "expense")
    .sort((a, b) => b.value - a.value)
    .map(({ node }) => node);

  const orderedNodes = [...incomeNodes, ...accountNodes, ...expenseNodes].map(
    (node) => ({
      ...node,
      x:
        node.category === "income"
          ? 20
          : node.category === "expense"
          ? 840
          : 400,
    })
  );

  const nodeIndexMap = Object.fromEntries(
    orderedNodes.map((node, index) => [node.name, index])
  );

  const updatedLinks = data.links
    .filter(
      (link) =>
        link.source != null &&
        link.target != null &&
        data.nodes[link.source] &&
        data.nodes[link.target]
    )
    .map((link) => ({
      ...link,
      source: nodeIndexMap[data.nodes[link.source].name],
      target: nodeIndexMap[data.nodes[link.target].name],
    }));

  return { nodes: orderedNodes, links: updatedLinks };
}

const LINK_LIMIT_OPTIONS = [
  { value: 25, label: "Top 25" },
  { value: 50, label: "Top 50" },
  { value: 75, label: "Top 75" },
  { value: 100, label: "Top 100" },
  { value: 150, label: "Top 150" },
  { value: 200, label: "Top 200" },
  { value: 0, label: "Show all" },
] as const;

const DEFAULT_LINK_LIMIT = 75;

/** Limit links to top N by value, keeping only connected nodes */
function limitLinksByValue(
  data: SankeyData,
  maxLinks: number
): SankeyData {
  if (maxLinks <= 0 || !data.links.length) return data;
  if (data.links.length <= maxLinks) return data;

  const sorted = [...data.links].sort(
    (a, b) => Math.abs(Number(b.value) || 0) - Math.abs(Number(a.value) || 0)
  );
  const topLinks = sorted.slice(0, maxLinks);

  const usedNodeIndices = new Set<number>();
  topLinks.forEach((link) => {
    usedNodeIndices.add(link.source as number);
    usedNodeIndices.add(link.target as number);
  });

  const keptIndices = Array.from(usedNodeIndices);
  const indexMap = new Map(keptIndices.map((idx, i) => [idx, i]));

  const filteredNodes = keptIndices.map((i) => data.nodes[i]);
  const filteredLinks = topLinks
    .filter(
      (link) =>
        indexMap.has(link.source as number) && indexMap.has(link.target as number)
    )
    .map((link) => ({
      ...link,
      source: indexMap.get(link.source as number)!,
      target: indexMap.get(link.target as number)!,
    }));

  return { nodes: filteredNodes, links: filteredLinks };
}

function SankeyChartInner({
  data,
  width = 1040,
  height = 500,
  maxLinks,
  onMaxLinksChange,
  onChartFilterChange,
}: {
  data: SankeyData;
  width: number;
  height: number;
  maxLinks: number;
  onMaxLinksChange: (value: number) => void;
  onChartFilterChange: (filter: ChartFilterChange) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const chartGroupRef = useRef<SVGGElement>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<
    SVGSVGElement,
    unknown
  > | null>(null);
  const [tooltip, setTooltip] = useState({
    show: false,
    content: "",
    x: 0,
    y: 0,
  });
  const [sankeyNodes, setSankeyNodes] = useState<any[]>([]);
  const [sankeyLinks, setSankeyLinks] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const limitedData = useMemo(
    () => limitLinksByValue(data, maxLinks),
    [data, maxLinks]
  );

  const sankeyResult = useMemo(() => {
    if (!limitedData || !limitedData.nodes || !limitedData.links) return null;
    const safeWidth = Math.max(width || 1040, 100);
    const safeHeight = Math.max(height || 500, 100);

    try {
      const accountNodes =
        limitedData.nodes.filter((n) => n.category === "account") || [];
      const incomeNodes = limitedData.nodes.filter((n) => n.category === "income") || [];
      const expenseNodes =
        limitedData.nodes.filter((n) => n.category === "expense") || [];

      const hasIncome = incomeNodes.length > 0;
      const accountStartColumn = hasIncome ? 1 : 0;
      const expenseColumn = hasIncome
        ? accountNodes.length + 1
        : accountNodes.length;

      const processedData = {
        nodes: (limitedData.nodes || []).map((node) => {
          let column;
          switch (node.category) {
            case "income":
              column = 0;
              break;
            case "account":
              const accountIndex = accountNodes.findIndex(
                (n) => n.name === node.name
              );
              column =
                accountIndex === -1
                  ? accountStartColumn
                  : accountStartColumn + accountIndex;
              break;
            case "expense":
              column = expenseColumn;
              break;
            default:
              column = 0;
          }
          return { ...node, id: node.name, column };
        }),
        links: (limitedData.links || [])
          .map((link) => {
            const sourceNode = limitedData.nodes[link.source]?.name
              ? limitedData.nodes[link.source]
              : limitedData.nodes.find((n) => n.name === link.source);
            const targetNode = limitedData.nodes[link.target]?.name
              ? limitedData.nodes[link.target]
              : limitedData.nodes.find((n) => n.name === link.target);
            if (!sourceNode || !targetNode) return null;
            return {
              ...link,
              source: link.source?.name || link.source,
              target: link.target?.name || link.target,
            };
          })
          .filter(Boolean),
      };

      if (!processedData.nodes.length || !processedData.links.length) {
        return null;
      }

      // Fast O(L) heuristic: remove links that flow backward (target.column < source.column).
      // Flow-of-funds is naturally left-to-right (income->accounts->expenses); cycles manifest
      // as backward links. This avoids the expensive O(L²) per-link cycle detection.
      const getColumn = (nodeOrIndex: any) => {
        const node =
          typeof nodeOrIndex === "number"
            ? processedData.nodes[nodeOrIndex]
            : nodeOrIndex;
        return node?.column ?? 0;
      };
      const finalLinks = processedData.links.filter((link: any) => {
        const srcCol = getColumn(link.source);
        const tgtCol = getColumn(link.target);
        return tgtCol >= srcCol;
      });

      if (finalLinks.length === 0) return { error: "noLinks" };

      const finalProcessedData = { ...processedData, links: finalLinks };

      const sankeyGenerator = sankey<SankeyNode, SankeyLink>()
        .nodeWidth(15)
        .nodePadding(10)
        .extent([
          [1, 5],
          [safeWidth - 1, safeHeight - 5],
        ])
        .nodeAlign((node: any) => node.column);

      const result = sankeyGenerator(finalProcessedData);

      const nodeColumnMap = new Map(
        processedData.nodes.map((n) => [n.id || n.name, n.column])
      );

      const maxColumn = Math.max(...processedData.nodes.map((n) => n.column));
      const columnCount = maxColumn + 1;
      const columnWidth =
        (safeWidth - 2) / Math.max(1, columnCount - 1);

      result.nodes.forEach((node: any) => {
        const originalColumn =
          node.column ?? nodeColumnMap.get(node.id || node.name) ?? 0;
        const xPosition =
          columnCount === 1 ? safeWidth / 2 : 1 + originalColumn * columnWidth;
        const nodeWidth = 15;
        node.x0 = xPosition - nodeWidth / 2;
        node.x1 = xPosition + nodeWidth / 2;
        node.column = originalColumn;
      });

      return result;
    } catch (err) {
      console.error("Flow diagram generation error:", err);
      return { error: "generationError" };
    }
  }, [limitedData, width, height]);

  useEffect(() => {
    if (sankeyResult && !(sankeyResult as any).error) {
      const r = sankeyResult as any;
      setSankeyNodes(r.nodes);
      setSankeyLinks(r.links);
      setError(null);
    } else if ((sankeyResult as any)?.error === "noLinks") {
      setSankeyNodes([]);
      setSankeyLinks([]);
      setError("noLinks");
    } else if ((sankeyResult as any)?.error === "generationError") {
      setSankeyNodes([]);
      setSankeyLinks([]);
      setError("generationError");
    }
  }, [sankeyResult]);

  useEffect(() => {
    if (!svgRef.current || !chartGroupRef.current || sankeyNodes.length === 0)
      return;

    const svg = d3.select(svgRef.current);
    const chartGroup = d3.select(chartGroupRef.current);

    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("start", () => {
        if (svgRef.current) svgRef.current.style.cursor = "grabbing";
      })
      .on("zoom", (event) => {
        const { x, y, k } = event.transform;
        chartGroup.attr("transform", `translate(${x},${y}) scale(${k})`);
      })
      .on("end", () => {
        if (svgRef.current) svgRef.current.style.cursor = "grab";
      });

    zoomBehaviorRef.current = zoomBehavior;
    svg.call(zoomBehavior);

    return () => {
      svg.on(".zoom", null);
    };
  }, [sankeyNodes, sankeyLinks, width, height]);

  if (!data || !data.nodes || !data.links) return null;

  if (error === "noLinks") {
    return (
      <div className="alert alert-warning" role="alert">
        No valid flow connections found after removing circular references.
      </div>
    );
  }

  if (error === "generationError") {
    return (
      <div className="alert alert-danger" role="alert">
        Error generating flow diagram.
      </div>
    );
  }

  if (sankeyNodes.length === 0 || sankeyLinks.length === 0) return null;

  const formatValue = (value: number) =>
    `$${Math.abs(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const nodes = sankeyNodes;
  const links = sankeyLinks;

  const getLinkColor = (link: any) => {
    const targetNode = link.target;
    const sourceNode = link.source;
    if (targetNode.category === "expense") return "#e74c3c";
    if (sourceNode.category === "income") return "#2ecc71";
    return "#3498db";
  };

  const resetZoom = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg
      .transition()
      .duration(750)
      .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  };

  const zoomIn = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(200).call(zoomBehaviorRef.current.scaleBy, 1.5);
  };

  const zoomOut = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg
      .transition()
      .duration(200)
      .call(zoomBehaviorRef.current.scaleBy, 1 / 1.5);
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          marginBottom: "10px",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          fontSize: "14px",
          color: "#495057",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px" }}>
          <div>
            <strong>How to use:</strong>
            <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
              <li>
                <strong>Zoom/Pan:</strong> Use the zoom controls (top right) or
                scroll to zoom, click and drag to pan
              </li>
              <li>
                <strong>Filter by category:</strong> Click on any link (income or
                expense) to filter transactions by that category
              </li>
              <li>
                <strong>Filter by account:</strong> Click on any account node to
                filter transactions for that account
              </li>
            </ul>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <Form.Label htmlFor="link-limit" style={{ marginBottom: 0, fontSize: "14px" }}>
              Max links:
            </Form.Label>
            <Form.Control
              as="select"
              id="link-limit"
              size="sm"
              value={maxLinks}
              onChange={(e) => onMaxLinksChange(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              {LINK_LIMIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Control>
            {maxLinks > 0 && data.links.length > maxLinks && (
              <span style={{ fontSize: "13px", color: "#6c757d" }}>
                (showing {links.length} of {data.links.length})
              </span>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          display: "flex",
          gap: "5px",
          background: "white",
          padding: "5px",
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Button
          variant="light"
          size="sm"
          onClick={zoomIn}
          title="Zoom In (or scroll wheel)"
          style={{ padding: "4px 8px" }}
        >
          <FontAwesomeIcon icon="search-plus" />
        </Button>
        <Button
          variant="light"
          size="sm"
          onClick={zoomOut}
          title="Zoom Out (or scroll wheel)"
          style={{ padding: "4px 8px" }}
        >
          <FontAwesomeIcon icon="search-minus" />
        </Button>
        <Button
          variant="light"
          size="sm"
          onClick={resetZoom}
          title="Reset Zoom"
          style={{ padding: "4px 8px" }}
        >
          <FontAwesomeIcon icon="undo" />
        </Button>
      </div>

      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ cursor: "grab", border: "1px solid #ddd" }}
      >
        <g ref={chartGroupRef} style={{ mixBlendMode: "multiply" }}>
          {links.map((link, i) => (
            <g
              key={i}
              onClick={() => {
                const src = link.source;
                const tgt = link.target;
                if (!src || !tgt) return;

                if (src.type === "accounts" && tgt.type === "accounts") {
                  onChartFilterChange({
                    selectedAccount: undefined,
                    selectedCategory: undefined,
                    tx1Account: src.name,
                    tx2Account: tgt.name,
                    linkFilterSource: undefined,
                    linkFilterDest: undefined,
                  });
                } else if (src.type === "categories" && tgt.type === "accounts") {
                  const categoryName =
                    (src.name.split(":")[1] || "").trim() || "Uncategorized";
                  onChartFilterChange({
                    selectedCategory: categoryName,
                    selectedAccount: tgt.name,
                    tx1Account: undefined,
                    tx2Account: undefined,
                    linkFilterSource: src.name,
                    linkFilterDest: tgt.name,
                  });
                } else if (src.type === "accounts" && tgt.type === "categories") {
                  const categoryName =
                    (tgt.name.split(":")[1] || "").trim() || "Uncategorized";
                  onChartFilterChange({
                    selectedCategory: categoryName,
                    selectedAccount: src.name,
                    tx1Account: undefined,
                    tx2Account: undefined,
                    linkFilterSource: src.name,
                    linkFilterDest: tgt.name,
                  });
                } else {
                  onChartFilterChange({
                    selectedAccount: undefined,
                    selectedCategory: undefined,
                    tx1Account: undefined,
                    tx2Account: undefined,
                    linkFilterSource: undefined,
                    linkFilterDest: undefined,
                  });
                }
              }}
              style={{ cursor: "pointer" }}
              onMouseEnter={(e) => {
                setTooltip({
                  show: true,
                  content: `${link.source.name} → ${link.target.name}\nAmount: ${formatValue(
                    link.value
                  )}\nClick to view transactions`,
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              onMouseLeave={() => setTooltip((t) => ({ ...t, show: false }))}
            >
              <path
                d={sankeyLinkHorizontal()(link)}
                style={{
                  fill: "none",
                  strokeOpacity: 0.5,
                  stroke: getLinkColor(link),
                  strokeWidth: Math.max(1, link.width),
                }}
              />
            </g>
          ))}

          {nodes.map((node, i) => (
            <g
              key={i}
              onClick={() => {
                const updates: ChartFilterChange = {
                  tx1Account: undefined,
                  tx2Account: undefined,
                };
                if (node.category === "income") {
                  updates.linkFilterSource = "Income";
                  updates.linkFilterDest = node.name;
                } else if (node.category === "expense") {
                  updates.linkFilterSource = node.name;
                  updates.linkFilterDest = "Expenses";
                }

                if (node.type === "categories") {
                  const categoryName =
                    (node.name.split(":")[1] || "").trim() || "Uncategorized";
                  updates.selectedCategory = categoryName;
                  updates.selectedAccount = undefined;
                } else if (node.type === "accounts") {
                  updates.selectedCategory = undefined;
                  updates.selectedAccount = node.name;
                }

                onChartFilterChange(updates);
              }}
              style={{ cursor: "pointer" }}
              onMouseEnter={(e) => {
                setTooltip({
                  show: true,
                  content: `${node.name} (${node.category})\nTotal Flow: ${formatValue(
                    node.value
                  )}\nInflow: ${formatValue(
                    node.targetLinks?.reduce((sum, l) => sum + l.value, 0) || 0
                  )}\nOutflow: ${formatValue(
                    node.sourceLinks?.reduce((sum, l) => sum + l.value, 0) || 0
                  )}\nClick to view transactions`,
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              onMouseLeave={() => setTooltip((t) => ({ ...t, show: false }))}
            >
              <rect
                x={node.x0}
                y={node.y0}
                width={node.x1 - node.x0}
                height={node.y1 - node.y0}
                fill="#666"
                opacity={0.8}
              />
              <text
                x={
                  node.category === "expense"
                    ? node.x0 - 6
                    : node.x0 < width / 2
                    ? node.x1 + 6
                    : node.x0 - 6
                }
                y={(node.y1 + node.y0) / 2}
                dy="0.35em"
                textAnchor={
                  node.category === "expense"
                    ? "end"
                    : node.x0 < width / 2
                    ? "start"
                    : "end"
                }
                fontSize={12}
                style={{ fill: "black" }}
              >
                {node.name}
                <tspan
                  x={
                    node.category === "expense"
                      ? node.x0 - 6
                      : node.x0 < width / 2
                      ? node.x1 + 6
                      : node.x0 - 6
                  }
                  dy="1.2em"
                >
                  {formatValue(Math.abs(node.value))}
                </tspan>
              </text>
            </g>
          ))}
        </g>
      </svg>

      {tooltip.show && (
        <div
          style={{
            position: "fixed",
            top: tooltip.y,
            left: tooltip.x,
            background: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "8px",
            borderRadius: "4px",
            fontSize: "12px",
            pointerEvents: "none",
            whiteSpace: "pre-line",
            transform: "translate(10px, -50%)",
            zIndex: 1000,
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
}

/** Chart component that does NOT subscribe to TransactionManager - isolated from table re-renders */
export const FlowOfFundsChart = React.memo(function FlowOfFundsChart({
  data,
  onChartFilterChange,
}: {
  data: SankeyData;
  onChartFilterChange: (filter: ChartFilterChange) => void;
}) {
  const [maxLinks, setMaxLinks] = useState(DEFAULT_LINK_LIMIT);

  return (
    <SizeMe
      refreshMode="debounce"
      refreshRate={250}
      noPlaceholder
    >
      {({ size }) => (
        <div style={{ width: "100%", height: "500px" }}>
          <SankeyChartInner
            data={data}
            width={size?.width ?? 1040}
            height={500}
            maxLinks={maxLinks}
            onMaxLinksChange={setMaxLinks}
            onChartFilterChange={onChartFilterChange}
          />
        </div>
      )}
    </SizeMe>
  );
});
